import React from "react";
import type { Result } from "@sequesign/verifier-browser";

// Canonical verification-level + badge copy, kept in sync with the other
// surfaces that show it:
//   apps/verifier/src/ui/VerificationLevelLegend.tsx
//   apps/dashboard/src/components/VerificationLevelLegend.tsx
//   src/viewer.html
// The demo used to invent its own L0–L5 "layer" numbering, which collided with
// this real ladder; it now surfaces the actual Level the verifier computed plus
// the independent badges. Editing the strings here without the other surfaces
// is a regression — they are duplicated by convention, not shared.

export const PREAMBLE =
  "Verification levels summarize the strongest identity-anchored claim that holds for this receipt: integrity, then agent identity, then policy. Witnessed, approval, and counterparty are shown as separate badges because each is an independent fact about the receipt, not a rung on the level ladder.";

export const ROWS: { level: string; label: string; description: string }[] = [
  {
    level: "L0",
    label: "Integrity Only",
    description:
      "The receipt is well-formed and its action chain is internally consistent. No further claims hold."
  },
  {
    level: "L1",
    label: "Witnessed",
    description:
      "Every action was co-signed by the witness identified in the receipt. Agent identity is not bound."
  },
  {
    level: "L2",
    label: "Key Bound",
    description:
      "Every action carries a valid agent signature bound to the receipt's key. Whether that key is a vouched identity is the separate Identity signal (registered vs self-asserted)."
  },
  {
    level: "L3",
    label: "Policy Bound",
    description: "Key bound, and every action commits to the policy context the task declared."
  }
];

export const BADGES_PREAMBLE =
  "Badges are independent facts shown alongside the level. Approval and counterparty are reported as present but unverified until the signer's identity is registered and vouched, so they never raise the level on their own.";

export const BADGES: { badge: string; description: string }[] = [
  {
    badge: "Witnessed",
    description:
      "Every action was co-signed by an independent witness. Shown alongside any level, since witnessing is a property of the chain."
  },
  {
    badge: "Approval",
    description: "A party, human or agent, signed an approval bound to an action type in the chain."
  },
  {
    badge: "Counterparty",
    description: "A counterparty signed a confirmation bound to a recorded action and its evidence."
  }
];

// Map the verifier's verification_level code to its canonical row (code +
// label). Anything unrecognized reads as "not established" rather than
// inventing a level.
function levelDisplay(level: string): { code: string; label: string } {
  if (level.startsWith("L3")) return { code: "L3", label: "Policy Bound" };
  if (level.startsWith("L2")) return { code: "L2", label: "Key Bound" };
  if (level.startsWith("L1")) return { code: "L1", label: "Witnessed" };
  if (level.startsWith("L0")) return { code: "L0", label: "Integrity Only" };
  return { code: "-", label: "Not established" };
}

// The badges that actually hold for this result. Witnessed is a property of
// the chain (any witness attestation); approval/counterparty come from signed
// legs. Each carries whether it is vouched, since an unvouched signer is
// present-but-unverified (never raising the level).
function activeBadges(result: Result): { label: string; note?: string }[] {
  const out: { label: string; note?: string }[] = [];
  if (result.trustAnchorMode === "self" || result.trustAnchorMode === "external") {
    out.push({ label: "Witnessed" });
  }
  if (result.approvals.length > 0) {
    const vouched = result.approvals.some((a) => a.vouched);
    out.push({ label: "Approval", note: vouched ? "vouched" : "self-asserted" });
  }
  if (result.counterparties.length > 0) {
    const vouched = result.counterparties.some((c) => c.vouched);
    out.push({ label: "Counterparty", note: vouched ? "vouched" : "self-asserted" });
  }
  return out;
}

// Level chip + independent badge chips, surfaced above the check list. Replaces
// the demo's old invented "layer level" number with the real one.
export function LevelSummary({ result }: { result: Result }) {
  const level = levelDisplay(result.level);
  const badges = activeBadges(result);
  return (
    <div className="level-summary">
      <div className="level-chip">
        <span className="level-chip-code">{level.code}</span>
        <span className="level-chip-label">{level.label}</span>
      </div>
      <div className="badge-chips">
        {badges.length === 0 ? (
          <span className="badge-chip badge-chip-none">No badges</span>
        ) : (
          badges.map((b) => (
            <span className="badge-chip" key={b.label}>
              {b.label}
              {b.note && <span className="badge-chip-note">{b.note}</span>}
            </span>
          ))
        )}
      </div>
    </div>
  );
}

export function VerificationLevelLegend() {
  return (
    <details className="level-legend">
      <summary className="level-legend-summary">What do these levels and badges mean?</summary>
      <p className="level-legend-preamble">{PREAMBLE}</p>
      <div className="level-legend-table-wrap">
        <table className="level-legend-table">
          <thead>
            <tr>
              <th scope="col">Level</th>
              <th scope="col">Label</th>
              <th scope="col">Description</th>
            </tr>
          </thead>
          <tbody>
            {ROWS.map((row) => (
              <tr key={row.level}>
                <td>{row.level}</td>
                <td>{row.label}</td>
                <td>{row.description}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="level-legend-preamble">{BADGES_PREAMBLE}</p>
      <div className="level-legend-table-wrap">
        <table className="level-legend-table">
          <thead>
            <tr>
              <th scope="col">Badge</th>
              <th scope="col">Meaning</th>
            </tr>
          </thead>
          <tbody>
            {BADGES.map((row) => (
              <tr key={row.badge}>
                <td>{row.badge}</td>
                <td>{row.description}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </details>
  );
}
